/**
 * RoleGate — Merchant Pro RBAC R-2 / RBAC-C1 route wrapper.
 *
 * Declarative route guard:
 *   <RoleGate allowedRoles={[...]}>                       // R-2 form
 *     <SomePage />
 *   </RoleGate>
 *
 *   <RoleGate requiredCapability="reservation.view_list"> // RBAC-C1 form
 *     <SomePage />
 *   </RoleGate>
 *
 * Behaviour:
 *   • auth still hydrating  → render `<BootLoader />` (matches App.js
 *     spinner so the visual handoff is seamless).
 *   • unauthenticated       → redirect to `/login` (preserves
 *     `state.from` so the staff login form can bounce back post-auth).
 *   • role NOT in allowedRoles → silent redirect to
 *     `landingForRole(user.role)` per OQ-R2-B locked default.
 *   • requiredCapability supplied AND user lacks it → silent redirect
 *     to `landingForRole(user.role)`. While capabilities are loading
 *     we render the boot loader instead of bouncing prematurely.
 *   • role IN allowedRoles AND (capability check passes OR cap not
 *     supplied) → render children.
 *
 * **Sub-phase RBAC-C1 (2026-06-18)** — added optional
 * `requiredCapability` prop. App.js may opt routes into capability-
 * driven guarding without rewriting the existing `allowedRoles` calls.
 * The two forms are NOT mutually exclusive: if both are supplied, the
 * actor must clear BOTH checks (role floor + capability overlay) —
 * mirrors the Option C backend semantics (require_roles is the floor,
 * require_capability is the additive overlay).
 *
 * **Tier-1 invariant** — capability checks use `usePermission(cap)`
 * (set membership), role checks use `allowedRoles.includes(role)`.
 * NEVER `role === '...'`. Keeps RoleGate forward-compatible with the
 * V2 capability resolver (§12 forward-looking directive · now V1
 * requirement post RBAC-C1).
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth';
import { landingForRole } from '@/lib/rbac';
import { useEffectiveCapabilities, usePermission } from '@/lib/capabilities';

function BootLoader() {
  return (
    <div
      className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"
      data-testid="role-gate-boot-loading"
    >
      <span className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

export default function RoleGate({ allowedRoles, requiredCapability, children }) {
  const { user, token, loading } = useAuthStore();
  const location = useLocation();
  const { isLoading: capsLoading } = useEffectiveCapabilities();
  const capAllowed = usePermission(requiredCapability);

  if (loading) return <BootLoader />;

  if (!token || !user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  // Role floor — R-2 contract (preserved verbatim).
  if (Array.isArray(allowedRoles) && allowedRoles.length > 0) {
    if (!allowedRoles.includes(user.role)) {
      // OQ-R2-B locked default — silent redirect to the actor's landing
      // page. The `state.denied` field is wired through but the toast
      // surface is deferred to V2 polish (operator decision).
      return (
        <Navigate
          to={landingForRole(user.role)}
          replace
          state={{ denied: location.pathname }}
        />
      );
    }
  }

  // Sub-phase RBAC-C1 capability overlay — only applies when the
  // route opted in via `requiredCapability`. Wait for capabilities to
  // resolve before bouncing — otherwise the boot navigation flashes
  // the user off the page they're authorized for.
  if (requiredCapability) {
    if (capsLoading) return <BootLoader />;
    if (!capAllowed) {
      return (
        <Navigate
          to={landingForRole(user.role)}
          replace
          state={{ denied: location.pathname, cap: requiredCapability }}
        />
      );
    }
  }

  return children;
}
